import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, fmtDate, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { StatusBadge } from "@finance/components/StatusBadge";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Checkbox } from "@finance/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@finance/components/ui/sheet";
import { ComplianceTab } from "@finance/components/ComplianceTab";
import { toast } from "sonner";
import { Plus, DownloadSimple, Trash, UsersThree, IdentificationCard, EnvelopeSimple } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { errMsg } from "@finance/lib/api";
import { BulkUploadButton } from "@finance/components/BulkUpload";

const FIELDS = [
  ["name", "Name"], ["tin", "TIN"], ["vrn", "VRN"], ["contact_person", "Contact Person"],
  ["phone", "Phone"], ["email", "Email(s) — separate multiple with commas; all receive statements"], ["postal_address", "Postal Address"],
  ["physical_address", "Physical Address"], ["ship_to_address", "Delivery / Ship-to Address (blank = same as Bill To)"], ["file_number", "File Number"],
];

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailTxns, setDetailTxns] = useState([]);
  const [detailGp, setDetailGp] = useState(null);
  const [sel, setSel] = useState([]);

  const load = useCallback(() => api.get("/clients").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (form.id) await api.put(`/clients/${form.id}`, form);
      else await api.post("/clients", form);
      toast.success("Client saved");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    setDetailGp(null);
    api.get(`/clients/${c.id}/profitability`).then((x) => setDetailGp(x.data)).catch(() => {});
    const r = await api.get(`/clients/${c.id}/transactions`);
    setDetailTxns(r.data);
  };

  const del = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const delSelected = async () => {
    if (!window.confirm(`Delete ${sel.length} selected client(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(sel.map((id) => api.delete(`/clients/${id}`)));
      toast.success(`${sel.length} client(s) deleted`);
      setSel([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cols = FIELDS.map(([key, label]) => ({ key, label }));

  return (
    <Layout>
      <PageHeader
        title="Clients"
        subtitle={`${rows.length} clients`}
        actions={
          <>
            {sel.length > 0 && <Button variant="outline" className="border-red-200 text-red-600" data-testid="delete-selected-clients" onClick={delSelected}><Trash size={16} className="mr-1" />Delete ({sel.length})</Button>}
            <Button variant="outline" data-testid="export-clients" onClick={() => exportToCsv("clients", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <BulkUploadButton entity="clients" onDone={load} />
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-client-btn" onClick={() => { setForm({}); setOpen(true); }}><Plus size={16} className="mr-1" />New Client</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={UsersThree} label="Total Clients" value={rows.length} testid="kpi-clients-count" />
          <KpiCard icon={IdentificationCard} label="With TIN" value={rows.filter((c) => c.tin).length} tone="info" testid="kpi-clients-tin" />
          <KpiCard icon={EnvelopeSimple} label="With Email" value={rows.filter((c) => c.email).length} tone="success" testid="kpi-clients-email" />
        </KpiStrip>
        {rows.length === 0 ? (
          <EmptyState icon={UsersThree} testid="clients-empty" title="No clients yet" message="Add your first client or bulk-upload a list to get started." />
        ) : (
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-3 py-2"><input type="checkbox" data-testid="select-all-clients" checked={rows.length > 0 && sel.length === rows.length} onChange={() => setSel(sel.length === rows.length ? [] : rows.map((r) => r.id))} /></th>
              <th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">TIN</th>
              <th className="px-4 py-2 text-left">VRN</th><th className="px-4 py-2 text-left">Contact</th>
              <th className="px-4 py-2 text-left">Phone</th><th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50" onClick={() => openDetail(c)}>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`select-client-${c.id}`} checked={sel.includes(c.id)} onChange={() => toggle(c.id)} /></td>
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2 font-mono-num">{c.tin || "—"}</td>
                  <td className="px-4 py-2 font-mono-num">{c.vrn || "—"}</td>
                  <td className="px-4 py-2">{c.contact_person || "—"}</td>
                  <td className="px-4 py-2 font-mono-num">{c.phone || "—"}</td>
                  <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-client-${c.id}`} onClick={() => { setForm(c); setOpen(true); }}>Edit</button>
                    <button className="ml-3 text-xs text-gray-400 hover:text-red-500" data-testid={`delete-client-${c.id}`} onClick={() => del(c)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map(([key, label]) => (
              <div key={key} className={key.includes("address") ? "col-span-2" : ""}>
                <Label className="lx-label">{label}</Label>
                <Input data-testid={`client-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" />
              </div>
            ))}
            <div className="col-span-2 mt-1 rounded-sm border border-gray-100 bg-gray-50 px-3 py-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox checked={form.auto_statements !== false} onCheckedChange={(v) => setForm({ ...form, auto_statements: !!v })} data-testid="client-auto-statements" />
                Include this client in automatic outstanding-statement emails
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-client" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
          <SheetHeader><SheetTitle className="font-display">{detail?.name}</SheetTitle></SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4">
              <Button size="sm" variant="outline" className="w-full" data-testid="client-statement-pdf" onClick={() => openPdf(`pdf/client-statement/${detail.id}`)}>Download Statement (PDF)</Button>
              {detailGp && (
                <div className="grid grid-cols-3 gap-2 rounded-sm border border-gray-200 p-3 text-sm" data-testid="client-profitability">
                  <div><div className="lx-label">Revenue</div><div className="lx-num" data-testid="client-gp-revenue">TZS {fmtNum(detailGp.revenue)}</div></div>
                  <div><div className="lx-label">Cost of sales</div><div className="lx-num" data-testid="client-gp-cost">TZS {fmtNum(detailGp.cost)}</div></div>
                  <div><div className="lx-label">Gross profit</div><div className={`lx-num font-semibold ${detailGp.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="client-gp-total">TZS {fmtNum(detailGp.gross_profit)}{detailGp.margin_pct != null ? ` · ${detailGp.margin_pct}%` : ""}{detailGp.estimated ? " (est.)" : ""}</div></div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="lx-label">TIN</div><div className="font-mono-num">{detail.tin || "—"}</div></div>
                <div><div className="lx-label">VRN</div><div className="font-mono-num">{detail.vrn || "—"}</div></div>
                <div><div className="lx-label">Contact</div><div>{detail.contact_person || "—"}</div></div>
                <div><div className="lx-label">Phone</div><div className="font-mono-num">{detail.phone || "—"}</div></div>
                <div><div className="lx-label">Auto-statements</div><div data-testid="client-detail-auto">{detail.auto_statements === false ? "Excluded" : "Included"}</div></div>
              </div>
              <div>
                <div className="lx-label mb-2">Transaction History ({detailTxns.length})</div>
                <div className="space-y-2">
                  {detailTxns.length === 0 && <div className="text-sm text-gray-400">No transactions.</div>}
                  {detailTxns.map((t) => (
                    <div key={t.id} className="rounded-sm border border-gray-200 p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono-num font-medium">{t.lx_po_no || t.client_po_no || "—"}</span>
                        <StatusBadge status={t.status} />
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{curSym(t.currency)} {fmtNum(t.total_amount)} · {fmtDate(t.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {detail && <div className="mt-4"><ComplianceTab ptype="client" pid={detail.id} /></div>}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
